import React from "react";
import { Alert, Card, Space, Table, Tag, Typography } from "antd";
import { Link } from "react-router-dom";
import routes from "../../../../engine/routes";
import { CONFIG_LABELS, fmtDay } from "../institutionMerge.constants";
import { languagesList } from "../../../../utils/constants";
import { InstitutionGrid } from "./InstitutionGrid";
import { afterCol, beforeCol } from "./recordDisplay";

const { Text } = Typography;
const OID = /^[a-f0-9]{24}$/i;
const BL_STATUS_FR = {
  active: "actif",
  inactive: "inactif",
  archived: "archivé",
};
const languageLabel = code => {
  const l = languagesList.find(x => x.value === code);
  return l ? l.label : String(code || "").toUpperCase();
};

const show = (v, names = {}) => {
  if (v === null || v === undefined || v === "")
    return <Text type="secondary">—</Text>;
  if (typeof v === "boolean") return v ? "oui" : "non";
  if (Array.isArray(v))
    return v.length ? (
      v.map(id => names[id] || String(id)).join(", ")
    ) : (
      <Text type="secondary">—</Text>
    );
  if (typeof v === "string" && OID.test(v))
    return names[v] || `…${v.slice(-6)}`;
  if (typeof v === "object" && "cloturePrestation" in v)
    return `clôture prestation : ${v.cloturePrestation ? "oui" : "non"}`;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};
const key = v => JSON.stringify(v === undefined ? null : v);

const addressLabel = a =>
  a.type === "maps"
    ? a.name || a.location || `…${String(a._id).slice(-6)}`
    : [
        a.name,
        [a.street, a.number].filter(Boolean).join(" "),
        [a.zip, a.city].filter(Boolean).join(" "),
      ]
        .filter(Boolean)
        .join(", ") || `…${String(a._id).slice(-6)}`;
const responsableLabel = r =>
  `${r.firstname || ""} ${r.lastname || ""}`.trim() ||
  r.email ||
  `…${String(r._id).slice(-6)}`;

/**
 * What happened to every address / responsable of the merged sites — kept, merged into another,
 * edited (field by field) — the operator's dependency decisions, restated from the decisions object.
 */
function DependencyDecisions({ title, items, decisions, labelOf, unit }) {
  const decs = decisions || {};
  const byId = new Map((items || []).map(i => [String(i._id), i]));
  const rows = (items || []).map(i => ({
    key: i._id,
    item: i,
    dec: decs[i._id] || { action: "keep" },
  }));
  const merged = rows.filter(r => r.dec.action === "merge").length;
  const edited = rows.filter(r => r.dec.edit && Object.keys(r.dec.edit).length)
    .length;
  return (
    <Card
      size="small"
      title={`${title} — ${rows.length} · ${merged} fusionné(e)s · ${edited} modifié(e)s`}
    >
      {rows.length === 0 ? (
        <Text type="secondary">
          Aucun
          {unit === "adresse" ? "e adresse concernée" : " responsable concerné"}
          .
        </Text>
      ) : (
        <Table
          size="small"
          pagination={rows.length > 25 ? { pageSize: 25 } : false}
          dataSource={rows}
          columns={[
            {
              title:
                unit === "adresse" ? "Adresse (avant)" : "Responsable (avant)",
              ...beforeCol,
              render: (_, r) => (
                <span>
                  {labelOf(r.item)}{" "}
                  <Text type="secondary">
                    ·{" "}
                    {r.item.owner
                      ? r.item.owner.name
                      : r.item.institution
                      ? r.item.institution.name
                      : ""}
                  </Text>
                </span>
              ),
            },
            {
              title: "Décision",
              render: (_, r) =>
                r.dec.action === "merge" ? (
                  <Tag color="orange">
                    fusionné{unit === "adresse" ? "e" : ""} dans{" "}
                    {byId.get(String(r.dec.into))
                      ? labelOf(byId.get(String(r.dec.into)))
                      : "?"}
                  </Tag>
                ) : (
                  <Tag>
                    rattaché
                    {unit === "adresse" ? "e telle quelle" : " tel quel"}
                  </Tag>
                ),
            },
            {
              title: "Modifications (avant → après)",
              render: (_, r) =>
                r.dec.edit && Object.keys(r.dec.edit).length ? (
                  <Space direction="vertical" size={0}>
                    {Object.entries(r.dec.edit).map(([f, v]) => (
                      <span key={f}>
                        <Text type="secondary">{f} : </Text>
                        <Text delete type="secondary">
                          {show(r.item[f])}
                        </Text>{" "}
                        → <Text strong>{show(v)}</Text>
                      </span>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary">—</Text>
                ),
            },
          ]}
        />
      )}
    </Card>
  );
}
const showUserField = (f, v, g, names) => {
  if (
    v === undefined ||
    v === null ||
    v === "" ||
    (Array.isArray(v) && !v.length)
  )
    return <Text type="secondary">—</Text>;
  if (f.kind === "account" || f.path === "credentials") {
    const a = (g.accounts || []).find(x => x._id === String(v));
    return a ? `mot de passe du compte ${a.institution.name}` : String(v);
  }
  if (f.kind === "date") return fmtDay(v);
  if (typeof v === "boolean") return v ? "oui" : "non";
  if (Array.isArray(v)) return v.map(x => names[x] || String(x)).join(", ");
  if (typeof v === "string" && OID.test(v))
    return names[v] || `…${v.slice(-6)}`;
  return String(v);
};

/**
 * Everything that was decided and what it produced — readable, not JSON:
 * which institutions were archived into what, every configuration field with the
 * candidates and the retained value (and whether it was a manual choice), the billing
 * timeline, each duplicate account with what survived and what was archived, and each
 * blacklist decision. This is the "what did we merge and how" record.
 */
export function MergeDecisions({ merge, onOpenJournal }) {
  const p = merge.preview;
  const d = merge.decisions || {};
  if (!p) return <Alert type="info" message="Prévisualisation indisponible." />;
  const names = (p.config && p.config.refNames) || {};
  const target = merge.targetInstitutionId;
  const detached = new Set((merge.detached || []).map(x => String(x.sourceId)));

  const configRows = Object.entries(p.config.fields)
    .filter(([field]) => field !== "billings")
    .map(([field, f]) => {
      // the name is decided as targetName (or kept by an existing target), not inside config
      const manual =
        field === "name"
          ? !p.existingTarget
          : d.config && d.config[field] !== undefined;
      const retained =
        field === "name"
          ? p.existingTarget
            ? p.existingTarget.name
            : d.targetName
          : manual
          ? d.config[field]
          : f.suggested;
      const custom =
        manual && !f.values.some(v => key(v.value) === key(retained));
      return {
        key: field,
        field,
        candidates: f.values,
        retained,
        origin: custom
          ? "saisi"
          : manual
          ? "choisi"
          : f.conflict
          ? "référence"
          : "identique",
        conflict: f.conflict,
      };
    });
  const metaLabel = Object.fromEntries(
    (p.config.meta || []).map(m => [m.field, m.label]),
  );
  // the billing timeline the target actually received: the decided one when the operator edited it
  // `billings: []` is a decision too: « aucune règle datée » — not the merged timeline
  const billingsAfter = Array.isArray(d.billings)
    ? d.billings.map(b => ({ ...b, decided: true }))
    : p.billings.timeline;
  const userFields = p.users.fields || [];
  const userNames = p.users.refNames || {};

  const dupRows = (p.users.duplicates || []).map(g => {
    const dec = (d.users || {})[g.email] || {};
    return { key: g.email, email: g.email, accounts: g.accounts, dec };
  });

  const blRows = (p.blacklists || []).map(b => ({
    key: b.interpreterId,
    ...b,
    dec: (d.blacklists || {})[b.interpreterId],
  }));

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card size="small" title="Institutions">
        <Table
          size="small"
          pagination={false}
          rowKey="_id"
          dataSource={p.sources}
          columns={[
            {
              title: "Établissement",
              dataIndex: "name",
              render: (n, s) => (
                <Link to={routes.institutions(s._id).detail.path}>{n}</Link>
              ),
            },
            {
              title: "Devenir",
              render: (_, s) =>
                detached.has(s._id) ? (
                  <Tag color="orange">détaché — de nouveau autonome</Tag>
                ) : merge.status === "restored" ? (
                  <Tag>restauré</Tag>
                ) : (
                  <Tag color="purple">
                    archivé → {target ? target.name : d.targetName}
                  </Tag>
                ),
            },
          ]}
        />
        <div style={{ marginTop: 8 }}>
          <Text strong>Institution résultante : </Text>
          {target ? (
            <Link to={routes.institutions(target._id).detail.path}>
              {target.name}
            </Link>
          ) : (
            <Text>{d.targetName || "—"}</Text>
          )}
          {merge.targetMode === "existing" ? (
            <Tag style={{ marginLeft: 8 }}>institution existante</Tag>
          ) : (
            <Tag style={{ marginLeft: 8 }} color="blue">
              créée par la fusion
            </Tag>
          )}
          {p.configDonor && (
            <Text type="secondary">
              {" "}
              · configuration de référence : {p.configDonor.name}
            </Text>
          )}
        </div>
        <div style={{ marginTop: 16 }}>
          <InstitutionGrid
            merge={merge}
            preview={p}
            onOpenJournal={onOpenJournal}
          />
        </div>
      </Card>

      <Card
        size="small"
        title="Configuration — ce que chaque établissement avait, ce qui a été retenu"
      >
        <Table
          size="small"
          pagination={false}
          dataSource={configRows}
          columns={[
            {
              title: "Champ",
              dataIndex: "field",
              width: 200,
              render: f => metaLabel[f] || CONFIG_LABELS[f] || f,
            },
            {
              title: "Valeurs par établissement",
              dataIndex: "candidates",
              ...beforeCol,
              render: (values, r) => {
                const distinct = [
                  ...new Map(values.map(v => [key(v.value), v])).values(),
                ];
                if (!r.conflict)
                  return (
                    <Text type="secondary">
                      {show(distinct[0].value, names)} — identique partout
                    </Text>
                  );
                return (
                  <Space direction="vertical" size={0}>
                    {distinct.map(v => (
                      <span key={key(v.value)}>
                        {key(v.value) === key(r.retained) ? (
                          show(v.value, names)
                        ) : (
                          <Text delete type="secondary">
                            {show(v.value, names)}
                          </Text>
                        )}{" "}
                        <Text type="secondary">
                          —{" "}
                          {values
                            .filter(x => key(x.value) === key(v.value))
                            .map(x => x.sourceName)
                            .join(", ")}
                        </Text>
                      </span>
                    ))}
                  </Space>
                );
              },
            },
            {
              title: "Retenu",
              dataIndex: "retained",
              width: 260,
              ...afterCol,
              render: (v, r) => (
                <Space>
                  <Text strong={r.conflict}>{show(v, names)}</Text>
                  {r.origin === "saisi" ? (
                    <Tag color="purple">valeur saisie</Tag>
                  ) : r.origin === "choisi" ? (
                    <Tag color="blue">choix manuel</Tag>
                  ) : r.origin === "référence" ? (
                    <Tag>référence</Tag>
                  ) : null}
                </Space>
              ),
            },
          ]}
        />
        {!(p.billings.perSource || []).some(s => s.rules.length > 0) &&
          billingsAfter.length === 0 && (
            <div style={{ marginTop: 12 }}>
              <Text strong>Règles de facturation : </Text>
              <Text type="secondary">
                aucune règle datée sur les établissements ni sur le résultat —
                le mode « Facturation groupée » retenu ci-dessus s'applique à
                toutes les périodes.
              </Text>
            </div>
          )}
        {(p.billings.perSource || []).some(s => s.rules.length > 0) &&
          billingsAfter.length === 0 && (
            <div style={{ marginTop: 12 }}>
              <Text strong>Règles de facturation — après : </Text>
              <Text type="secondary">
                aucune règle datée (retirées à la fusion) — le mode «
                Facturation groupée » retenu ci-dessus s'applique à toutes les
                périodes.
              </Text>
            </div>
          )}
        {(p.billings.perSource || []).some(s => s.rules.length > 0) && (
          <div style={{ marginTop: 12 }}>
            <Text strong>Règles de facturation — avant, par établissement</Text>
            <Space wrap size={[8, 8]} style={{ marginTop: 4 }}>
              {p.billings.perSource.map(s => (
                <Text key={s.sourceId} type="secondary">
                  {s.sourceName} :{" "}
                  {s.rules.length
                    ? s.rules
                        .map(
                          r =>
                            `${r.year}-${String(r.month).padStart(2, "0")} ${
                              r.type === "multiple"
                                ? "groupée"
                                : "par prestation"
                            }`,
                        )
                        .join(", ")
                    : "aucune règle"}
                </Text>
              ))}
            </Space>
          </div>
        )}
        {billingsAfter.length > 0 && (
          <Table
            style={{ marginTop: 12 }}
            size="small"
            pagination={false}
            title={() =>
              "Règles de facturation — après (chronologie appliquée)"
            }
            rowKey={t => `${t.year}-${t.month}`}
            dataSource={billingsAfter}
            columns={[
              {
                title: "Mois",
                render: (_, t) =>
                  `${t.year}-${String(t.month).padStart(2, "0")}`,
              },
              {
                title: "Mode retenu",
                ...afterCol,
                render: (_, t) => {
                  const chosen = (d.billings || []).find(
                    b => b.year === t.year && b.month === t.month,
                  );
                  const type =
                    t.decided || t.conflict ? chosen && chosen.type : t.type;
                  const union = p.billings.timeline.find(
                    u => u.year === t.year && u.month === t.month,
                  );
                  const edited =
                    t.decided &&
                    (!union || union.conflict || union.type !== type);
                  return (
                    <Space>
                      {type === "multiple"
                        ? "groupée"
                        : type === "unique"
                        ? "par prestation"
                        : "—"}
                      {(t.conflict || edited) && (
                        <Tag color="blue">choix manuel</Tag>
                      )}
                    </Space>
                  );
                },
              },
              {
                title: "Établissements (avant)",
                ...beforeCol,
                render: (_, t) => {
                  const u =
                    p.billings.timeline.find(
                      x => x.year === t.year && x.month === t.month,
                    ) || t;
                  if (!u.options && !u.sources)
                    return "règle ajoutée à la fusion";
                  return u.conflict
                    ? u.options
                        .map(
                          o =>
                            `${
                              o.type === "multiple"
                                ? "groupée"
                                : "par prestation"
                            } : ${o.sources.map(s => s.name).join(", ")}`,
                        )
                        .join(" · ")
                    : (u.sources || []).map(s => s.name).join(", ");
                },
              },
            ]}
          />
        )}
      </Card>

      <Card size="small" title={`Comptes en double — ${dupRows.length}`}>
        {dupRows.length === 0 ? (
          <Text type="secondary">
            Aucun compte partagé entre les établissements.
          </Text>
        ) : (
          dupRows.map(g => (
            <Card
              key={g.email}
              size="small"
              type="inner"
              style={{ marginBottom: 8 }}
              title={
                <Space>
                  <Text code>{g.email}</Text>
                  {g.dec.action === "merge" ? (
                    <Tag color="purple">fusionnés</Tag>
                  ) : g.dec.action === "keep" ? (
                    <Tag>gardés séparés</Tag>
                  ) : (
                    <Tag color="red">non décidé</Tag>
                  )}
                </Space>
              }
            >
              <Table
                size="small"
                pagination={false}
                rowKey="_id"
                dataSource={g.accounts}
                columns={[
                  {
                    title: "Compte",
                    render: (_, a) => (
                      <span>
                        {a.name || <Text type="secondary">sans nom</Text>}{" "}
                        <Text type="secondary">
                          · {a.institution.name}
                          {a.institution.isTarget ? " (cible existante)" : ""}
                        </Text>
                      </span>
                    ),
                  },
                  {
                    title: "Rôle",
                    render: (_, a) => (
                      <Space size={4}>
                        {a.role}
                        {(a.subRoles || []).map(s => (
                          <Tag key={s}>{s}</Tag>
                        ))}
                      </Space>
                    ),
                  },
                  {
                    title: "Rattachements avant",
                    dataIndex: "references",
                    align: "right",
                  },
                  {
                    title: "Devenir",
                    render: (_, a) => {
                      if (g.dec.action === "merge")
                        return a._id === g.dec.survivorId ? (
                          <Tag color="green">conservé — reçoit tout</Tag>
                        ) : (
                          <Tag color="default">
                            archivé, e-mail neutralisé, rattachements transférés
                          </Tag>
                        );
                      if (g.dec.action === "keep")
                        return g.dec.rename &&
                          g.dec.rename.accountId === a._id ? (
                          <Tag color="blue">
                            renommé en {g.dec.rename.email}
                          </Tag>
                        ) : (
                          <Tag>inchangé</Tag>
                        );
                      return null;
                    },
                  },
                ]}
              />
              {g.dec.roles && (
                <Text type="secondary">
                  Rôles retenus pour le compte conservé : {g.dec.roles.role} ·{" "}
                  {(g.dec.roles.subRoles || []).join(", ")}{" "}
                  <Tag color="blue">choix manuel</Tag>
                </Text>
              )}
              {g.dec.action === "merge" && userFields.length > 0 && (
                <Table
                  style={{ marginTop: 8 }}
                  size="small"
                  pagination={false}
                  title={() => "Champs du compte conservé — avant / après"}
                  rowKey="path"
                  dataSource={userFields.filter(f =>
                    g.accounts.some(a => {
                      const v = a.fields && a.fields[f.path];
                      return (
                        v !== undefined &&
                        v !== null &&
                        v !== "" &&
                        !(Array.isArray(v) && !v.length)
                      );
                    }),
                  )}
                  columns={[
                    { title: "Champ", dataIndex: "label", width: 180 },
                    ...g.accounts.map(a => ({
                      title: `Avant — ${a.institution.name}`,
                      ...beforeCol,
                      render: (_, f) => {
                        const v = a.fields && a.fields[f.path];
                        const decided =
                          g.dec.fields && g.dec.fields[f.path] !== undefined;
                        const survivor = g.accounts.find(
                          x => x._id === g.dec.survivorId,
                        );
                        const after = decided
                          ? g.dec.fields[f.path]
                          : survivor && survivor.fields
                          ? survivor.fields[f.path]
                          : undefined;
                        const differing = (g.differing || []).includes(f.path);
                        // struck through when this account's value is not the one the kept account ends up with
                        return differing && key(v) !== key(after) ? (
                          <Text delete type="secondary">
                            {showUserField(f, v, g, userNames)}
                          </Text>
                        ) : (
                          showUserField(f, v, g, userNames)
                        );
                      },
                    })),
                    {
                      title: "Après — compte conservé",
                      ...afterCol,
                      render: (_, f) => {
                        const survivor = g.accounts.find(
                          a => a._id === g.dec.survivorId,
                        );
                        const decided =
                          g.dec.fields && g.dec.fields[f.path] !== undefined;
                        const v = decided
                          ? g.dec.fields[f.path]
                          : survivor && survivor.fields
                          ? survivor.fields[f.path]
                          : undefined;
                        const differing = (g.differing || []).includes(f.path);
                        return (
                          <Space>
                            <Text strong={differing}>
                              {showUserField(f, v, g, userNames)}
                            </Text>
                            {decided ? (
                              <Tag color="blue">choix manuel</Tag>
                            ) : differing ? (
                              <Tag color="red">non décidé</Tag>
                            ) : (
                              <Tag>identique</Tag>
                            )}
                          </Space>
                        );
                      },
                    },
                  ]}
                />
              )}
            </Card>
          ))
        )}
      </Card>

      {p.addresses && Array.isArray(p.addresses.items) && (
        <DependencyDecisions
          title="Adresses"
          unit="adresse"
          items={p.addresses.items}
          decisions={d.addresses}
          labelOf={addressLabel}
        />
      )}
      {p.responsables && Array.isArray(p.responsables.items) && (
        <DependencyDecisions
          title="Responsables"
          unit="responsable"
          items={p.responsables.items}
          decisions={d.responsables}
          labelOf={responsableLabel}
        />
      )}

      <Card
        size="small"
        title={`Listes noires d'interprètes — ${blRows.length}`}
      >
        {blRows.length === 0 ? (
          <Text type="secondary">
            Aucun interprète exclu par l'un des établissements.
          </Text>
        ) : (
          <Table
            size="small"
            pagination={false}
            dataSource={blRows}
            columns={[
              {
                title: "Interprète",
                dataIndex: "name",
                width: 230,
                render: (n, b) => (
                  <Space direction="vertical" size={0}>
                    <Space size={6}>
                      <Text strong>{n}</Text>
                      {b.status && (
                        <Tag
                          style={{ whiteSpace: "nowrap" }}
                          color={
                            b.archived || b.status === "archived"
                              ? "default"
                              : b.status === "active"
                              ? "green"
                              : "orange"
                          }
                        >
                          {BL_STATUS_FR[b.status] || b.status}
                        </Tag>
                      )}
                    </Space>
                    {b.email && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {b.email}
                      </Text>
                    )}
                    {(b.languages || []).length > 0 && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        langues : {b.languages.map(languageLabel).join(", ")}
                      </Text>
                    )}
                  </Space>
                ),
              },
              {
                title: "Exclu·e par",
                dataIndex: "excludedAt",
                ...beforeCol,
                render: (xs, b) => (
                  <Space direction="vertical" size={4}>
                    <Space wrap size={4}>
                      {(xs || []).map(x => (
                        <Tag
                          key={x._id}
                          color={x.isTarget ? "orange" : "default"}
                        >
                          {x.name}
                          {x.isTarget ? " — institution qui reçoit" : ""}
                        </Tag>
                      ))}
                      {/* older previews: the receiver only as a flag */}
                      {b.excludedAtTarget &&
                        !(xs || []).some(x => x.isTarget) && (
                          <Tag color="orange">
                            déjà exclu·e par l'institution qui reçoit
                          </Tag>
                        )}
                    </Space>
                    {(b.excludedElsewhere || []).length > 0 && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        aussi exclu·e ailleurs :{" "}
                        {b.excludedElsewhere.map(x => x.name).join(", ")} (hors
                        fusion, inchangé)
                      </Text>
                    )}
                  </Space>
                ),
              },
              {
                title: "A travaillé pour",
                dataIndex: "workedAt",
                width: 240,
                ...beforeCol,
                render: xs =>
                  xs && xs.length ? (
                    <Space direction="vertical" size={0}>
                      {xs.map(x => (
                        <Text key={x._id}>
                          {x.name}
                          {x.isTarget ? (
                            <Text type="secondary">
                              {" "}
                              (institution qui reçoit)
                            </Text>
                          ) : null}{" "}
                          <Text strong>{x.n}×</Text>
                          {x.last ? (
                            <Text type="secondary">
                              {" "}
                              · dernière le {fmtDay(x.last)}
                            </Text>
                          ) : null}
                        </Text>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">jamais dans ces établissements</Text>
                  ),
              },
              {
                title: "Après",
                dataIndex: "dec",
                width: 230,
                ...afterCol,
                render: (v, b) => {
                  const t = target ? target.name : d.targetName;
                  const before = (b.excludedAt || [])
                    .map(x =>
                      x.isTarget
                        ? `${x.name} (institution qui reçoit)`
                        : x.name,
                    )
                    .join(", ");
                  if (v === "keep")
                    return (
                      <Text>
                        exclu·e chez : <Text strong>{t}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          avant : {before}
                        </Text>
                      </Text>
                    );
                  if (v === "drop")
                    return (
                      <Text>
                        <Text strong>plus exclu·e</Text>{" "}
                        <Text type="secondary">
                          (de nouveau disponible pour {t})
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          avant : {before}
                        </Text>
                      </Text>
                    );
                  return <Text type="secondary">—</Text>;
                },
              },
              {
                title: "Décision",
                dataIndex: "dec",
                width: 200,
                render: v =>
                  v === "keep" ? (
                    <Tag color="green">
                      exclusion gardée sur l'institution résultante
                    </Tag>
                  ) : v === "drop" ? (
                    <Tag color="orange">
                      exclusion retirée — de nouveau disponible
                    </Tag>
                  ) : (
                    <Tag color="red">non décidé</Tag>
                  ),
              },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}
